import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "../../lib/api";
import { PageHeader, Loading, Money, Empty } from "../../components/common";

const DOTS = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-rose-500", blue: "bg-sky-500" };
const LEGEND = [["green", "Complete"], ["yellow", "Upcoming"], ["red", "Late"], ["blue", "Scheduled / Filing"]];

export default function TaxCalendar() {
  const [events, setEvents] = useState(null);
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

  useEffect(() => { API.get("/calendar/events").then((r) => setEvents(r.data.events)); }, []);

  const days = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const startDay = first.getDay();
    const count = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= count; d++) cells.push(d);
    return cells;
  }, [cursor]);

  const byDate = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      if (!e.date) return;
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const move = (dir) => {
    let { year, month } = cursor;
    month += dir;
    if (month < 0) { month = 11; year -= 1; }
    if (month > 11) { month = 0; year += 1; }
    setCursor({ year, month });
  };

  if (!events) return <Loading label="Loading calendar" />;
  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const iso = (d) => `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const monthEvents = events.filter((e) => e.date && e.date.startsWith(`${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}`));

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Tax Calendar" sub="Filing, payment, and document deadlines — email and SMS reminders configurable in Settings."
        right={<div className="flex flex-wrap gap-3">{LEGEND.map(([c, l]) => (
          <span key={c} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
            <span className={`h-2.5 w-2.5 rounded-full ${DOTS[c]}`} />{l}</span>))}</div>} />

      <div className="card-gov p-4">
        <div className="mb-3 flex items-center justify-between">
          <button className="btn-outline-gov px-2 py-1" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <h3 className="font-bold text-navy" data-testid="cal-month-label">{monthLabel}</h3>
          <button className="btn-outline-gov px-2 py-1" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200" data-testid="calendar-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="bg-slate-50 px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">{d}</div>
          ))}
          {days.map((d, i) => {
            const dayEvents = d ? byDate[iso(d)] || [] : [];
            const isToday = d && iso(d) === today.toISOString().slice(0, 10);
            return (
              <div key={i} className={`min-h-20 bg-white p-1.5 ${isToday ? "ring-2 ring-inset ring-gold" : ""}`}>
                {d && <div className={`text-xs font-semibold ${isToday ? "text-copper" : "text-slate-500"}`}>{d}</div>}
                <div className="mt-1 space-y-1">
                  {dayEvents.slice(0, 3).map((e, j) => (
                    <div key={j} className={`flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium ${DOTS[e.color]} bg-opacity-15`} title={e.title}>
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOTS[e.color]}`} />
                      <span className="truncate text-slate-700">{e.title}</span>
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="text-[10px] text-slate-400">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-3 font-bold text-navy">{monthLabel} Deadlines</h3>
        {monthEvents.length === 0 ? <Empty title="No deadlines this month" /> : (
          <div className="space-y-2">
            {monthEvents.map((e, i) => (
              <div key={i} className="card-gov flex items-center justify-between px-4 py-3" data-testid={`event-${i}`}>
                <div className="flex items-center gap-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${DOTS[e.color]}`} />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{e.title}</div>
                    <div className="text-xs text-slate-400">{e.date} · {e.status?.replace(/_/g, " ")}</div>
                  </div>
                </div>
                {e.amount_cents != null && <Money cents={e.amount_cents} className="font-bold text-navy" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
